import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "../lib/api";
import { analyzeSeo, homeHtml, slugify } from "../lib/seo";
import { parseSchema } from "../lib/head";
import { Card, Field, PageTitle, Btn, Toggle, inputCls } from "./ui";
import { SeoReport } from "./SeoReport";
import { ImageField } from "./ImageField";

const TABS = [["contacts", "Contact & Social"], ["branding", "Branding / Favicon"], ["seo", "Homepage SEO"], ["integrations", "Search & Analytics"], ["performance", "Speed / CDN"], ["effects", "Graphics & Effects"], ["ai", "AI"], ["account", "Account"]];

const Inputs = ({ obj, onChange, fields, prefix }) => (
  <div className="grid gap-4 sm:grid-cols-2">
    {fields.map(([k, label, hint]) => (
      <Field key={k} label={label} hint={hint}>
        <input value={obj[k] || ""} onChange={(e) => onChange({ ...obj, [k]: e.target.value })} className={inputCls} data-testid={`${prefix}-${k}`} />
      </Field>
    ))}
  </div>
);

const Account = () => {
  const [p, setP] = useState({ current_password: "", new_password: "" });
  const submit = async () => {
    try {
      await api.post("/auth/change-password", p);
      toast.success("Password updated");
      setP({ current_password: "", new_password: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Change password">
      <div className="grid max-w-xl gap-4 sm:grid-cols-2">
        <Field label="Current password"><input type="password" value={p.current_password} onChange={(e) => setP({ ...p, current_password: e.target.value })} className={inputCls} data-testid="current-password" /></Field>
        <Field label="New password"><input type="password" value={p.new_password} onChange={(e) => setP({ ...p, new_password: e.target.value })} className={inputCls} data-testid="new-password" /></Field>
      </div>
      <Btn className="mt-4" onClick={submit} data-testid="change-password">Update password</Btn>
    </Card>
  );
};

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [models, setModels] = useState({});
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("contacts");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    api.get("/admin/settings").then(({ data }) => { if (live) { setS(data.settings); setModels(data.ai_models); } });
    api.get("/admin/content").then(({ data }) => { if (live) setContent(data.en); });
    return () => { live = false; };
  }, []);

  const homeResult = useMemo(() => content && s && analyzeSeo({ title: content.seo.title, metaDesc: content.seo.description, slug: slugify(s.home_seo.focus_keyword),
    html: homeHtml(content), keyword: s.home_seo.focus_keyword, image: s.home_seo.og_image || "default", schema: true }), [content, s]);

  const save = async () => {
    if (s.home_seo.schema_json.trim() && !parseSchema(s.home_seo.schema_json).length) return toast.error("Homepage schema is not valid JSON");
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Settings saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!s) return <p className="text-zinc-500">Loading…</p>;
  const up = (k) => (v) => setS({ ...s, [k]: v });
  return (
    <div data-testid="admin-settings">
      <PageTitle title="Settings" sub="Links, SEO, integrations, performance and AI">
        {tab !== "account" && <Btn onClick={save} disabled={saving} data-testid="settings-save">{saving ? "Saving…" : "Save settings"}</Btn>}
      </PageTitle>
      <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`settings-tab-${k}`}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider ${tab === k ? "bg-[#D4AF37] text-black" : "border border-[#D4AF37]/30 text-[#D4AF37]"}`}>{l}</button>
        ))}
      </div>
      {tab === "contacts" && (
        <div className="space-y-6">
          <Card title="Contact buttons">
            <Inputs prefix="contacts" obj={s.contacts} onChange={up("contacts")} fields={[["whatsapp", "WhatsApp URL", "e.g. https://wa.me/91XXXXXXXXXX?text=Hi"], ["telegram", "Telegram URL", "e.g. https://t.me/yourhandle"], ["signal", "Signal URL", "e.g. https://signal.me/#p/+91XXXXXXXXXX"], ["login", "Login button URL"], ["join", "Join button URL"]]} />
          </Card>
          <Card title="Social profiles (shown in footer)">
            <Inputs prefix="social" obj={s.social} onChange={up("social")} fields={[["facebook", "Facebook"], ["instagram", "Instagram"], ["x", "X / Twitter"], ["youtube", "YouTube"], ["pinterest", "Pinterest"]]} />
            <p className="mt-3 text-xs text-zinc-500">The footer WhatsApp icon uses the WhatsApp contact link above. Empty profiles are hidden.</p>
          </Card>
        </div>
      )}
      {tab === "branding" && (
        <Card title="Favicon (browser tab icon)">
          <div className="grid gap-6 sm:grid-cols-[220px_1fr]">
            <ImageField value={s.branding.favicon} onChange={(v) => up("branding")({ ...s.branding, favicon: v })} testid="favicon" />
            <div className="space-y-3 text-sm text-zinc-400">
              <div className="flex items-center gap-3">
                <img src={s.branding.favicon ? fileUrl(s.branding.favicon) : "/favicon-32x32.png"} alt="Current favicon" className="h-12 w-12 rounded-lg ring-1 ring-[#D4AF37]/40" data-testid="favicon-current" />
                <span>{s.branding.favicon ? "Custom favicon" : "Default gold “D” favicon"}</span>
              </div>
              <p className="text-xs text-zinc-500">Upload a square PNG or ICO (512×512 recommended). Leave empty to use the built-in gold “D” icon. Save settings to apply. Browsers cache favicons — a refresh may be needed to see the change.</p>
            </div>
          </div>
        </Card>
      )}
      {tab === "seo" && homeResult && (
        <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
          <Card title="Homepage SEO">
            <div className="space-y-4">
              <Field label="Focus keyword"><input value={s.home_seo.focus_keyword} onChange={(e) => up("home_seo")({ ...s.home_seo, focus_keyword: e.target.value })} className={inputCls} data-testid="home-keyword" /></Field>
              <p className="text-xs text-zinc-500">Homepage title and description are edited in <b>Homepage → Homepage SEO title & description</b>. Current title: “{content.seo.title}”</p>
              <Field label="Social share image URL"><input value={s.home_seo.og_image} onChange={(e) => up("home_seo")({ ...s.home_seo, og_image: e.target.value })} className={inputCls} data-testid="home-og-image" /></Field>
              <Field label="Extra JSON-LD schema" hint="Added on top of the built-in Organization, WebSite and FAQ schema.">
                <textarea rows={8} value={s.home_seo.schema_json} onChange={(e) => up("home_seo")({ ...s.home_seo, schema_json: e.target.value })} spellCheck={false} className={`${inputCls} font-mono text-xs`} data-testid="home-schema" />
              </Field>
            </div>
          </Card>
          <SeoReport result={homeResult} testid="home-seo-report" />
        </div>
      )}
      {tab === "integrations" && (
        <Card title="Google Search Console, Bing & Google Analytics">
          <Inputs prefix="integrations" obj={s.integrations} onChange={up("integrations")} fields={[
            ["gsc_verification", "Google Search Console code", "Content value of the google-site-verification meta tag"],
            ["bing_verification", "Bing Webmaster code", "Content value of the msvalidate.01 meta tag"],
            ["ga_id", "Google Analytics ID", "e.g. G-XXXXXXXXXX"]]} />
          <p className="mt-5 text-xs text-zinc-500">Submit your sitemap in GSC and Bing: <span className="font-mono text-[#D4AF37]">{window.location.origin}/api/sitemap.xml</span></p>
        </Card>
      )}
      {tab === "performance" && (
        <Card title="Speed & mobile performance">
          <div className="space-y-5">
            <Toggle checked={s.performance.lazyload} onChange={(v) => up("performance")({ ...s.performance, lazyload: v })} testid="perf-lazyload" label="Lazy-load images (recommended for mobile)" />
            <Field label="CDN base URL (optional)" hint="e.g. https://cdn.daddyanna247.com — uploaded images will load from this CDN. Point the CDN origin at your site domain.">
              <input value={s.performance.cdn_base_url} onChange={(e) => up("performance")({ ...s.performance, cdn_base_url: e.target.value })} className={inputCls} data-testid="perf-cdn" />
            </Field>
            <p className="text-xs text-zinc-500">Uploaded images are served with 1-year browser cache headers so CDNs and phones keep them cached.</p>
          </div>
        </Card>
      )}
      {tab === "effects" && (
        <Card title="Gold animations">
          <div className="space-y-5">
            <Toggle checked={s.effects.particles} onChange={(v) => up("effects")({ ...s.effects, particles: v })} testid="fx-particles-toggle" label="Floating gold particles (hero, stats bar, ratings)" />
            <Toggle checked={s.effects.glow} onChange={(v) => up("effects")({ ...s.effects, glow: v })} testid="fx-glow-toggle" label="Gold glow on cards, icons and headings" />
            <Toggle checked={s.effects.countup} onChange={(v) => up("effects")({ ...s.effects, countup: v })} testid="fx-countup-toggle" label="Count-up animation on numbers" />
            <p className="text-xs text-zinc-500">Images and icons for each section are changed in <b>Homepage</b> — open a section to upload images or pick icons. Animations are skipped automatically for visitors who prefer reduced motion.</p>
          </div>
        </Card>
      )}
      {tab === "ai" && (
        <Card title="AI blog writer">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Provider">
              <select value={s.ai.provider} onChange={(e) => up("ai")({ ...s.ai, provider: e.target.value, model: models[e.target.value][0] })} className={inputCls} data-testid="ai-provider">
                <option value="gemini">Google Gemini</option><option value="claude">Anthropic Claude</option>
              </select>
            </Field>
            <Field label="Model">
              <select value={s.ai.model} onChange={(e) => up("ai")({ ...s.ai, model: e.target.value })} className={inputCls} data-testid="ai-model">
                {(models[s.ai.provider] || []).map((m) => <option key={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Default editor mode">
              <select value={s.ai.mode} onChange={(e) => up("ai")({ ...s.ai, mode: e.target.value })} className={inputCls} data-testid="ai-mode">
                <option value="ai">AI generated</option><option value="plain">Plain HTML</option>
              </select>
            </Field>
          </div>
          <p className="mt-4 text-xs text-zinc-500">Runs on your Emergent universal key — no separate API key needed.</p>
        </Card>
      )}
      {tab === "account" && <Account />}
    </div>
  );
}
